import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  X,
  FileText,
  Calendar,
  Building2,
  CheckCircle2,
  Package,
  ChevronLeft,
  PlusCircle,
  Clock,
  Layout,
  Receipt,
  BadgeCheck,
  Save
} from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import SelectCompanyModal from '../components/SelectCompanyModal';
import SelectProductModal from '../components/SelectProductModal';
import { purchaseOrdersApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

function AddPurchaseOrder() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    billNumber: '',
    billDate: new Date().toISOString().split('T')[0],
    billedOn: new Date().toISOString().split('T')[0],
    partnerId: null,
    partnerName: '',
    gstNumber: '',
    paymentStatus: 'PENDING',
    paymentAmount: 0,
    status: 'ACTIVE',
    products: []
  });
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectCompany = (company) => {
    setFormData(prev => ({
      ...prev,
      partnerId: company.id,
      partnerName: company.companyName,
      gstNumber: company.gstNumber
    }));
    setShowCompanyModal(false);
  };

  const handleAddProduct = (product) => {
    // Add product with default values
    const newProduct = {
      ...product,
      uniqueId: Date.now(),
      quantity: product.quantity || 1,
      price: product.finalPrice || product.price || 0,
      discountPercent: product.discount || 0,
      gstPercent: 12.00, // Default GST 12%
      batchNumber: product.batchNumber || '',
      expiryDate: '', // User can add later
      mrp: product.finalPrice || product.price || 0
    };

    // Calculate amount
    newProduct.amount = calculateItemAmount(newProduct);

    setFormData(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
  };

  const handleRemoveProduct = (uniqueId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.uniqueId !== uniqueId)
    }));
  };

  const calculateItemAmount = (product) => {
    const quantity = Number(product.quantity) || 0;
    const price = Number(product.price) || 0;
    const discountPercent = Number(product.discountPercent) || 0;
    const gstPercent = Number(product.gstPercent) || 12;

    // Calculate: (Price * Quantity) - Discount + GST
    const subtotal = price * quantity;
    const discountAmount = (subtotal * discountPercent) / 100;
    const afterDiscount = subtotal - discountAmount;
    const gstAmount = (afterDiscount * gstPercent) / 100;
    const total = afterDiscount + gstAmount;

    return parseFloat(total.toFixed(2));
  };

  const handleUpdateProduct = (uniqueId, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (p.uniqueId === uniqueId) {
          const updated = { ...p, [field]: value };
          // Recalculate amount whenever any field changes
          updated.amount = calculateItemAmount(updated);
          return updated;
        }
        return p;
      })
    }));
  };

  const calculateSubtotal = () => {
    return formData.products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  const calculateTax = () => {
    return formData.products.reduce((sum, p) => {
      const itemSubtotal = p.price * p.quantity;
      const discountAmount = (itemSubtotal * (p.discountPercent || 0)) / 100;
      return sum + ((itemSubtotal - discountAmount) * (p.gstPercent || 12)) / 100;
    }, 0);
  };

  const calculateDiscount = () => {
    return formData.products.reduce((sum, p) => sum + ((p.price * p.quantity * (p.discountPercent || 0)) / 100), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateTax();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.billNumber) {
      message.warning('Bill Number is COMPULSORY. Please enter it.');
      return;
    }

    if (!formData.partnerId) {
      message.warning('Please select a supplier company first.');
      setShowCompanyModal(true);
      return;
    }

    if (formData.products.length === 0) {
      message.warning('Add at least one product before saving.');
      setShowProductModal(true);
      return;
    }

    let finalGstNumber = formData.gstNumber;
    if (finalGstNumber) {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(finalGstNumber)) {
        finalGstNumber = '22AAAAA0000A1Z5'; // Auto-fallback to default valid GST
      }
    } else {
      finalGstNumber = '22AAAAA0000A1Z5';
    }

    try {
      setLoading(true);

      const totalAmount = calculateTotal();
      const purchaseOrderData = {
        billNumber: formData.billNumber,
        partnerId: formData.partnerId,
        gstNumber: finalGstNumber,
        billDate: formData.billDate,
        billedOn: formData.billedOn,
        paymentStatus: formData.paymentStatus,
        paymentAmount: parseFloat(formData.paymentAmount) || 0,
        status: formData.status,
        totalAmount: totalAmount,
        outstandingAmount: totalAmount,
        purchaseItems: formData.products.map(product => ({
          productId: product.id,
          quantity: parseInt(product.quantity) || 0,
          price: parseFloat(product.price) || 0,
          discountPercent: parseFloat(product.discountPercent) || 0,
          gstPercent: parseFloat(product.gstPercent) || 12.00,
          batchNumber: product.batchNumber || '',
          expiryDate: product.expiryDate || null,
          mrp: parseFloat(product.mrp) || parseFloat(product.price) || 0
        }))
      };

      console.log('Final Purchase Order Payload:', purchaseOrderData);

      // Save to localStorage so it appears in the list 
      await new Promise(resolve => setTimeout(resolve, 800));
      const existingOrders = JSON.parse(localStorage.getItem('mock_purchase_orders') || '[]');
      const newMockOrder = {
        id: Date.now(),
        invoiceNumber: purchaseOrderData.billNumber,
        companyName: formData.partnerName || 'Unknown Company',
        gstin: purchaseOrderData.gstNumber,
        netAmount: purchaseOrderData.totalAmount,
        dateTime: new Date(purchaseOrderData.billDate).toLocaleString(),
        poNumber: `PO-${Math.floor(Math.random() * 90000) + 10000}`,
        status: 'PENDING',
        scheduledDate: 'Pending'
      };
      localStorage.setItem('mock_purchase_orders', JSON.stringify([newMockOrder, ...existingOrders]));

      message.success('Bill #' + formData.billNumber + ' saved successfully!');
      navigate('/purchase');
    } catch (error) {
      console.error('Save Error:', error);
      message.error(error.message || 'Server error occurred while saving. Check internet connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F4F9F8] min-h-screen p-8 font-sans">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-6">
          <button
            onClick={() => navigate('/purchase/orders')}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-[#E1F2ED] shadow-sm hover:bg-gray-50 transition-all"
          >
            <ChevronLeft size={24} className="text-[#4B9C86]" />
          </button>
          <div className="flex items-center space-x-3">
            <span className="text-[24px] font-black text-[#1E4D43]">#{formData.billNumber || '00000'}</span>
          </div>
        </div>
        <div className="bg-[#E6F4F1] text-[#4B9C86] px-4 py-1.5 rounded-full text-sm font-bold shadow-sm">
          Delivered
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Column */}
        <div className="flex-1 space-y-8">
          {/* Bill Details Card */}
          <div className="bg-white rounded-[1.5rem] p-8 border border-[#E1F2ED] shadow-sm">
            <div className="flex items-center space-x-3 mb-8">
              <Receipt size={22} className="text-[#4B9C86]" />
              <h2 className="text-[18px] font-black text-[#1E4D43] uppercase tracking-wide">Bill Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              {/* Bill Number */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-gray-400 uppercase">BILL NUMBER <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    name="billNumber"
                    value={formData.billNumber}
                    onChange={handleChange}
                    placeholder="Enter bill number"
                    className="w-full px-4 py-3 bg-[#F2F4F7] border border-transparent rounded-xl focus:outline-none text-[#1E4D43] font-bold placeholder:text-gray-300"
                  />
                </div>
              </div>

              {/* Bill Date */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-gray-400 uppercase">BILL DATE</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                    <Calendar size={18} className="text-[#4B9C86]" />
                  </div>
                  <input
                    type="date"
                    name="billDate"
                    value={formData.billDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-xl focus:outline-none focus:border-[#4B9C86] text-[#1E4D43] font-bold"
                  />
                </div>
              </div>

              {/* Choose Company */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-gray-400 uppercase">CHOOSE COMPANY</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                    <Search size={18} className="text-[#4B9C86]" />
                  </div>
                  <input
                    type="text"
                    value={formData.partnerName}
                    readOnly
                    onClick={() => setShowCompanyModal(true)}
                    placeholder="Search or select supplier"
                    className="w-full pl-12 pr-4 py-3 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-xl focus:outline-none focus:border-[#4B9C86] cursor-pointer text-[#1E4D43] font-bold placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* GST Number */}
              <div className="space-y-2">
                <label className="text-[12px] font-bold text-gray-400 uppercase">GST NUMBER</label>
                <input
                  type="text"
                  value={formData.gstNumber}
                  readOnly
                  placeholder="22AAAAA0000A1Z5"
                  className="w-full px-4 py-3 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-xl focus:outline-none text-[#1E4D43] font-bold placeholder:text-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Add Products Card */}
          <div className="bg-white rounded-[1.5rem] p-8 border border-[#E1F2ED] shadow-sm min-h-[400px] flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <PlusCircle size={22} className="text-[#4B9C86]" />
                <h2 className="text-[18px] font-black text-[#1E4D43] uppercase tracking-wide">Products List</h2>
              </div>

              <button
                onClick={() => setShowProductModal(true)}
                className="flex items-center space-x-2 px-6 py-2 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-full text-[#4B9C86] font-bold hover:bg-[#EBF5F1] transition-all shadow-sm"
              >
                <PlusCircle size={18} />
                <span>Add Product</span>
              </button>
            </div>

            {formData.products.length > 0 ? (
              <div className="flex-1 space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-[#E1F2ED]">
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider">Product Info</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-center">Qty</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-right">Price</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-right">Disc. %</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-right">GST %</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-right">Total</th>
                        <th className="pb-4 text-[11px] font-black text-gray-400 uppercase tracking-wider text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E1F2ED]">
                      {formData.products.map((product) => (
                        <tr key={product.uniqueId} className="group hover:bg-[#F9FBFA] transition-colors">
                          <td className="py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-[#F2FBF9] rounded-lg border border-[#E1F2ED] flex items-center justify-center p-1 overflow-hidden">
                                {product.imageUrl ? (
                                  <img src={product.imageUrl} alt="" className="w-full h-full object-contain" />
                                ) : (
                                  <Package size={18} className="text-[#4B9C86]" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-black text-[#1E4D43]">{product.productName}</p>
                                <p className="text-[10px] font-bold text-gray-400">BATCH: {product.batchNumber || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 text-center">
                            <input
                              type="number"
                              value={product.quantity}
                              onChange={(e) => handleUpdateProduct(product.uniqueId, 'quantity', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 bg-[#F2FBF9] border border-[#E1F2ED] rounded-lg text-center font-bold text-[#1E4D43] focus:outline-none focus:border-[#4B9C86]"
                            />
                          </td>
                          <td className="py-4 text-right">
                            <input
                              type="number"
                              value={product.price}
                              onChange={(e) => handleUpdateProduct(product.uniqueId, 'price', parseFloat(e.target.value) || 0)}
                              className="w-24 px-2 py-1 bg-[#F2FBF9] border border-[#E1F2ED] rounded-lg text-right font-bold text-[#1E4D43] focus:outline-none focus:border-[#4B9C86]"
                            />
                          </td>
                          <td className="py-4 text-right">
                            <input
                              type="number"
                              value={product.discountPercent}
                              onChange={(e) => handleUpdateProduct(product.uniqueId, 'discountPercent', parseFloat(e.target.value) || 0)}
                              className="w-16 px-2 py-1 bg-[#F2FBF9] border border-[#E1F2ED] rounded-lg text-right font-bold text-[#1E4D43] focus:outline-none focus:border-[#4B9C86]"
                            />
                          </td>
                          <td className="py-4 text-right text-sm font-bold text-[#1E4D43]">
                            {product.gstPercent}%
                          </td>
                          <td className="py-4 text-right">
                            <span className="text-sm font-black text-[#4B9C86]">₹ {product.amount.toFixed(2)}</span>
                          </td>
                          <td className="py-4 text-right">
                            <button
                              onClick={() => handleRemoveProduct(product.uniqueId)}
                              className="p-2 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            >
                              <X size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-[#E1F2ED] rounded-[1.5rem] bg-[#F9FBFA] p-12">
                <div className="w-16 h-16 bg-[#EBF5F1] rounded-full flex items-center justify-center text-[#4B9C86] mb-4">
                  <Package size={32} />
                </div>
                <p className="text-[18px] font-bold text-[#1E4D43]">No Products Added</p>
                <p className="text-gray-400 text-sm mt-1">Select items to create a new purchase order</p>

                <button
                  onClick={() => setShowProductModal(true)}
                  className="mt-6 font-black text-[#4B9C86] hover:underline"
                >
                  Browse Products
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="w-full lg:w-[400px]">
          <div className="bg-white rounded-[1.5rem] p-8 border border-[#E1F2ED] shadow-sm flex flex-col h-full">
            <div className="flex items-center space-x-3 mb-10">
              <div className="w-8 h-8 bg-[#E6F4F1] rounded flex items-center justify-center text-[#4B9C86]">
                <BadgeCheck size={20} />
              </div>
              <h2 className="text-[18px] font-black text-[#1E4D43]">Order Summary</h2>
            </div>

            <div className="space-y-6 flex-1">
              <div className="flex justify-between items-center text-gray-500 font-bold">
                <span className="text-sm">Subtotal</span>
                <span className="text-[16px]">₹ {calculateSubtotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-gray-500 font-bold">
                <span className="text-sm">Tax (GST 12%)</span>
                <span className="text-[16px]">₹ {calculateTax().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-gray-500 font-bold">
                <span className="text-sm">Discount</span>
                <span className="text-[16px]">-₹ {calculateDiscount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="border-t border-[#E1F2ED] pt-6 mb-8">
                <p className="text-[12px] font-bold text-gray-400 uppercase mb-2">TOTAL AMOUNT</p>
                <p className="text-[42px] font-black text-[#10B981]">
                  ₹ {calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2, minimumIntegerDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="bg-[#F2FBF9] border border-[#E1F2ED] rounded-2xl p-4 mb-8">
              <p className="text-[11px] font-bold text-[#1E4D43] leading-relaxed text-center opacity-70">
                By clicking save, the inventory will be updated automatically with the mentioned quantities.
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`w-full py-4 rounded-2xl flex items-center justify-center space-x-3 transition-all shadow-md active:scale-[0.98] ${loading ? 'bg-gray-200 text-gray-400' : 'bg-[#BDDBCE] text-[#1E4D43] hover:bg-[#A9CCBD]'}`}
            >
              <Save size={20} />
              <span className="text-[16px] font-black uppercase tracking-wider">{loading ? 'Saving...' : 'Save Purchase'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Select Company Modal */}
      <SelectCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSelect={handleSelectCompany}
      />

      {/* Select Product Modal */}
      <SelectProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onAdd={handleAddProduct}
      />
    </div>
  );
}

export default AddPurchaseOrder;